import { type CSSProperties, type FC, memo, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import type { DataTableCellReference } from '@/components/UI/DataTableV2/DataTable.model';
import { useDataTable } from '../DataTableProvider';
import { useRemoteSelectionInteraction } from '../RemoteSelectionInteractionContext';
import { gridPositionKey, type RemoteSelectionCell } from '../hooks/useRemoteSelections';
import { UserPresenceChip } from '../DataTableOnlineUsers/UserPresenceChip';
import styles from './RemoteSelectionsOverlay.module.scss';

interface UserListProps {
  cell: RemoteSelectionCell;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

/** Static list of the users sharing a cell, shown inside the "N users" popover. */
const UserList: FC<UserListProps> = ({ cell, onMouseEnter, onMouseLeave }) => (
  <ul
    className={styles.userList}
    data-testid="remote-selection-user-list"
    onMouseEnter={onMouseEnter}
    onMouseLeave={onMouseLeave}
  >
    {cell.users.map((user) => (
      <li key={user.userId} className={styles.userRow}>
        <UserPresenceChip name={user.userName} color={user.color} isActive showTooltip={false} showStatusDot={false} />
        <span className={styles.userName}>{user.userName}</span>
      </li>
    ))}
  </ul>
);

interface RemoteSelectionCellViewProps {
  cell: RemoteSelectionCell;
  nameChipHeight: number;
  isHovered: boolean;
  isPulsed: boolean;
  isFollowed: boolean;
  onPopoverEnter: (cellRef: DataTableCellReference) => void;
  onPopoverLeave: () => void;
}

/**
 * Renders one cell's selection rectangle and (when emphasised) its name chip.
 *
 * Memoised so a hover change on one cell doesn't rebuild the style objects of
 * every other cell: the three `CSSProperties` are computed here via `useMemo`
 * keyed on the cell geometry and the emphasis flags, and the parent passes
 * stable popover-hover setters.
 */
const RemoteSelectionCellView: FC<RemoteSelectionCellViewProps> = memo(
  ({ cell, nameChipHeight, isHovered, isPulsed, isFollowed, onPopoverEnter, onPopoverLeave }) => {
    const isEmphasized = isHovered || isFollowed;
    const backgroundAlpha = isEmphasized || isPulsed ? 15 : 5;
    const multiUser = cell.users.length > 1;
    const showChip = isEmphasized;

    const selectionStyle = useMemo<CSSProperties>(
      () => ({
        top: `${cell.top}px`,
        left: `${cell.left}px`,
        width: `${cell.width}px`,
        height: `${cell.height}px`,
        borderColor: cell.color.border,
        backgroundColor: `color-mix(in srgb, ${cell.color.border} ${backgroundAlpha}%, transparent)`,
      }),
      [cell.top, cell.left, cell.width, cell.height, cell.color.border, backgroundAlpha]
    );

    const nameChipContainerStyle = useMemo<CSSProperties>(
      () => ({
        top: `${cell.top - nameChipHeight}px`,
        left: `${cell.left}px`,
        width: `${cell.width}px`,
        height: `${nameChipHeight}px`,
      }),
      [cell.top, cell.left, cell.width, nameChipHeight]
    );

    const nameChipStyle = useMemo<CSSProperties>(
      () => ({ backgroundColor: cell.color.border, height: `${nameChipHeight}px` }),
      [cell.color.border, nameChipHeight]
    );

    const chipLabel = multiUser ? `${cell.users.length} users` : cell.users[0].userName;
    const popoverId = `remote-selection-popover--${cell.cellRef}`;

    return (
      <div>
        <div
          className={cn(styles.remoteSelection, {
            [styles.emphasized]: isEmphasized,
            [styles.pulse]: isPulsed,
          })}
          style={selectionStyle}
          data-testid={`remote-selection--${cell.cellRef}`}
        />
        {showChip && (
          <div
            className={styles.nameChipContainer}
            style={nameChipContainerStyle}
            onMouseEnter={() => onPopoverEnter(cell.cellRef)}
            onMouseLeave={onPopoverLeave}
          >
            <div
              className={styles.nameChip}
              style={nameChipStyle}
              data-testid="remote-selection-name-chip"
              data-tooltip-id={multiUser ? popoverId : undefined}
            >
              <span className={styles.nameChipLabel}>{chipLabel}</span>
            </div>
            {multiUser && (
              <ReactTooltip
                id={popoverId}
                place="right"
                clickable
                isOpen={isHovered}
                className={styles.popover}
                render={() => (
                  <UserList
                    cell={cell}
                    onMouseEnter={() => onPopoverEnter(cell.cellRef)}
                    onMouseLeave={onPopoverLeave}
                  />
                )}
              />
            )}
          </div>
        )}
      </div>
    );
  }
);

/**
 * Renders one colored rectangle and name chip per cell that has remote
 * selections. The name chip (a single user's name, or "<count> users" with a
 * popover listing everyone) is only shown while the pointer is over the cell
 * itself, while the user's avatar is hovered, or while following the user.
 *
 * The selection rectangles use pointer-events: none so the local user can still
 * select the cell underneath. Because of that we cannot rely on DOM hover for
 * the rectangle, so cell hover is detected by tracking the pointer over the
 * scroll container and resolving the cell in O(1) via `cellRefByPosition`.
 *
 * The container's bounding rect is cached and only refreshed on scroll/resize
 * (not per pointer move), and pointer hit-testing is batched to one pass per
 * animation frame.
 *
 * Positioned absolutely within the grid's virtualContainer so it scrolls with
 * the content. Pulse/follow highlighting is resolved via O(1) cellRef lookups.
 */
export const RemoteSelectionsOverlay: FC = () => {
  const { dataTableService } = useDataTable();
  const { selections, pulsedUserId, followedUserId } = useRemoteSelectionInteraction();
  const [hoveredCellRef, setHoveredCellRef] = useState<DataTableCellReference | null>(null);
  const [popoverHoveredCellRef, setPopoverHoveredCellRef] = useState<DataTableCellReference | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const selectionsRef = useRef(selections);
  selectionsRef.current = selections;
  const { cellWidth, cellHeight } = dataTableService.dimensions;

  const nameChipHeight = cellHeight / 2;
  const pulsedCellRef = pulsedUserId !== null ? selections.userToCellRef.get(pulsedUserId) : undefined;
  const followedCellRef = followedUserId !== null ? selections.userToCellRef.get(followedUserId) : undefined;

  const popoverHandlers = useMemo(
    () => ({
      onPopoverEnter: (cellRef: DataTableCellReference) => setPopoverHoveredCellRef(cellRef),
      onPopoverLeave: () => setPopoverHoveredCellRef(null),
    }),
    []
  );

  useEffect(() => {
    // The virtualContainer is the full-size content that scrolls inside the grid
    // viewport, so its bounding rect already reflects the scroll offset: the
    // pointer's content coordinate is simply clientX/Y minus the rect origin.
    const virtualContainer = containerRef.current?.parentElement ?? null;
    if (virtualContainer === null) {
      return;
    }

    // Cache the rect: it only changes on scroll/resize, never on pointer move,
    // so reading it per mousemove would force a needless layout flush each time.
    let rect = virtualContainer.getBoundingClientRect();
    const refreshRect = (): void => {
      rect = virtualContainer.getBoundingClientRect();
    };

    let frame = 0;
    let lastEvent: MouseEvent | null = null;
    const processMove = (): void => {
      frame = 0;
      if (lastEvent === null) {
        return;
      }
      const columnIndex = Math.floor((lastEvent.clientX - rect.left) / cellWidth);
      const rowIndex = Math.floor((lastEvent.clientY - rect.top) / cellHeight);
      const cellRef = selectionsRef.current.cellRefByPosition.get(gridPositionKey(columnIndex, rowIndex)) ?? null;
      setHoveredCellRef((current) => (current === cellRef ? current : cellRef));
    };

    const handleMouseMove = (event: MouseEvent): void => {
      lastEvent = event;
      if (frame === 0) {
        frame = requestAnimationFrame(processMove);
      }
    };

    const handleMouseLeave = (): void => {
      setHoveredCellRef(null);
    };

    const resizeObserver = new ResizeObserver(refreshRect);
    resizeObserver.observe(virtualContainer);
    window.addEventListener('scroll', refreshRect, true);
    virtualContainer.addEventListener('mousemove', handleMouseMove);
    virtualContainer.addEventListener('mouseleave', handleMouseLeave);
    return () => {
      if (frame !== 0) {
        cancelAnimationFrame(frame);
      }
      resizeObserver.disconnect();
      window.removeEventListener('scroll', refreshRect, true);
      virtualContainer.removeEventListener('mousemove', handleMouseMove);
      virtualContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [cellWidth, cellHeight]);

  return (
    <div ref={containerRef} data-testid="remote-selections-overlay">
      {selections.cells.map((cell) => (
        <RemoteSelectionCellView
          key={cell.cellRef}
          cell={cell}
          nameChipHeight={nameChipHeight}
          isHovered={hoveredCellRef === cell.cellRef || popoverHoveredCellRef === cell.cellRef}
          isPulsed={pulsedCellRef === cell.cellRef}
          isFollowed={followedCellRef === cell.cellRef}
          onPopoverEnter={popoverHandlers.onPopoverEnter}
          onPopoverLeave={popoverHandlers.onPopoverLeave}
        />
      ))}
    </div>
  );
};
